import { useState, useEffect, useCallback } from "react";
import { storyElement } from "@/lib/story-element";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, CheckCircle2, XCircle, Circle, Plus, UserCheck, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";

interface QuestObjective {
  text: string;
  done?: boolean;
  location?: string;
}

interface Quest {
  id: string;
  quest_key: string;
  title: string;
  description: string;
  category: string;
  status: string;
  objectives: QuestObjective[];
  rewards?: string;
  location_tags: string[];
  created_by?: string;
  created_at: string;
  completed_at?: string;
  player_accepted?: boolean;
  personal_status?: string;
  objective_progress?: QuestObjective[];
  accepted_player_count?: number;
}

interface QuestJournalProps {
  quests?: any[];
  adventureId?: string;
  playerId?: string;
  isMultiplayer?: boolean;
  onWidgetAction?: (action: any, params: Record<string, string>) => void;
  widgetActions?: any[];
}

const TAB_KEYS = ["available", "active", "complete", "failed"] as const;

export function QuestJournal({ quests: legacyQuests, adventureId, playerId, isMultiplayer, onWidgetAction }: QuestJournalProps) {
  const { t } = useLocale();
  const TAB_LABELS: Record<string, string> = {
    available: t.play_available,
    active: t.play_active,
    complete: t.play_complete,
    failed: t.play_failed,
  };

  const [tab, setTab] = useState<typeof TAB_KEYS[number]>("active");
  const [dbQuests, setDbQuests] = useState<Quest[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [customQuestText, setCustomQuestText] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchQuests = useCallback(async () => {
    if (!adventureId) return;
    const { data: questRows } = await supabase
      .from("adventure_quests" as any)
      .select("*")
      .eq("adventure_id", adventureId)
      .order("created_at", { ascending: true });

    if (!questRows) return;

    const playerAcceptance: Record<string, any> = {};
    const acceptCounts: Record<string, number> = {};
    if (playerId) {
      const { data: pqData } = await supabase
        .from("adventure_quest_players" as any)
        .select("*")
        .in("quest_id", questRows.map((q: any) => q.id));

      if (pqData) {
        for (const pq of pqData as any[]) {
          acceptCounts[pq.quest_id] = (acceptCounts[pq.quest_id] || 0) + 1;
          if (pq.player_id === playerId) {
            playerAcceptance[pq.quest_id] = pq;
          }
        }
      }
    }

    const mapped: Quest[] = (questRows as any[]).map((q) => ({
      ...q,
      objectives: Array.isArray(q.objectives) ? q.objectives : [],
      location_tags: Array.isArray(q.location_tags) ? q.location_tags : [],
      player_accepted: !!playerAcceptance[q.id],
      personal_status: playerAcceptance[q.id]?.personal_status,
      objective_progress: playerAcceptance[q.id]?.objective_progress,
      accepted_player_count: acceptCounts[q.id] || 0,
    }));

    setDbQuests(mapped);
  }, [adventureId, playerId]);

  useEffect(() => { fetchQuests(); }, [fetchQuests]);

  useEffect(() => {
    if (!adventureId) return;
    const channel = supabase
      .channel(`quests-${adventureId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_quests", filter: `adventure_id=eq.${adventureId}` }, () => fetchQuests())
      // Filter by player_id when available so we only receive changes for this player,
      // not every row across all adventures (audit item #14).
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_quest_players", ...(playerId ? { filter: `player_id=eq.${playerId}` } : {}) }, () => fetchQuests())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [adventureId, fetchQuests, playerId]);

  const handleAcceptQuest = async (quest: Quest) => {
    if (!playerId || quest.player_accepted) return;
    setLoading(true);
    try {
      await supabase.from("adventure_quest_players" as any).insert({
        quest_id: quest.id,
        player_id: playerId,
        objective_progress: quest.objectives.map((o) => ({ text: o.text, done: false })),
      });
      if (quest.status === "available") {
        await supabase.from("adventure_quests" as any)
          .update({ status: "active", updated_at: new Date().toISOString() })
          .eq("id", quest.id);
      }
      if (onWidgetAction) {
        onWidgetAction(
          { id: "accept_quest", messageTemplate: 'I accept the quest "{{quest_name}}".' },
          { quest_name: quest.title },
        );
      }
      fetchQuests();
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuest = () => {
    if (!customQuestText.trim() || !onWidgetAction) return;
    onWidgetAction(
      { id: "create_quest", messageTemplate: 'I want to take on a personal quest: "{{quest_description}}"' },
      { quest_description: customQuestText.trim() },
    );
    setCustomQuestText("");
    setShowCreateForm(false);
  };

  const useDbQuests = dbQuests.length > 0 || adventureId;

  const legacyNormalized: Quest[] = !useDbQuests
    ? (legacyQuests || []).map((q: any, i: number) => {
        if (typeof q === "string") return { id: String(i), quest_key: String(i), title: q, description: "", category: "side", status: "active", objectives: [], location_tags: [], created_at: "" };
        return { id: q.id || String(i), quest_key: q.id || String(i), title: q.title || q.name || "Quest", description: q.description || "", category: "side", status: q.status || "active", objectives: q.objectives || [], location_tags: [], created_at: "" };
      })
    : [];

  const allQuests = useDbQuests ? dbQuests : legacyNormalized;

  const filtered = allQuests.filter((q) => {
    if (tab === "active") return q.status === "active" && (q.player_accepted !== false || !useDbQuests);
    if (tab === "available") return q.status === "available" || (q.status === "active" && q.player_accepted === false);
    if (tab === "complete") return q.status === "complete";
    if (tab === "failed") return q.status === "failed";
    return false;
  });

  const counts = {
    available: allQuests.filter((q) => q.status === "available" || (q.status === "active" && q.player_accepted === false)).length,
    active: allQuests.filter((q) => q.status === "active" && (q.player_accepted !== false || !useDbQuests)).length,
    complete: allQuests.filter((q) => q.status === "complete").length,
    failed: allQuests.filter((q) => q.status === "failed").length,
  };

  return (
    <div {...storyElement("quest-journal")} className="space-y-3">
      <div className="flex gap-1 p-0.5 rounded-lg bg-muted/30 border border-border/30">
        {TAB_KEYS.map((tk) => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            className={cn(
              "flex-1 text-[10px] px-2 py-1 rounded-md transition-all capitalize font-medium",
              tab === tk
                ? "bg-primary/15 text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/20",
            )}
          >
            {TAB_LABELS[tk]} {counts[tk] > 0 && <span className="ml-0.5 text-[9px] opacity-70">({counts[tk]})</span>}
          </button>
        ))}
      </div>

      {onWidgetAction && (
        <div className="flex gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[10px] gap-1 text-muted-foreground hover:text-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <Plus className="w-3 h-3" /> {t.play_create_quest}
          </Button>
        </div>
      )}

      {showCreateForm && (
        <div className="flex gap-1.5 items-center">
          <Input
            value={customQuestText}
            onChange={(e) => setCustomQuestText(e.target.value)}
            placeholder={t.play_quest_goal}
            className="h-7 text-xs"
            onKeyDown={(e) => e.key === "Enter" && handleCreateQuest()}
          />
          <Button size="sm" className="h-7 text-[10px]" onClick={handleCreateQuest} disabled={!customQuestText.trim()}>
            {t.play_go}
          </Button>
        </div>
      )}

      <div className="space-y-2 max-h-64 overflow-auto">
        {filtered.length === 0 && (
          <div className="flex flex-col items-center py-6 gap-1.5 text-muted-foreground/50">
            <ScrollText className="w-6 h-6" />
            <p className="text-xs">{tFormat(t.play_no_quests, { tab: TAB_LABELS[tab] })}</p>
          </div>
        )}
        {filtered.map((q) => {
          const objectives = q.objective_progress && q.objective_progress.length > 0 ? q.objective_progress : q.objectives;
          const doneCount = objectives.filter((o) => o.done).length;
          const totalCount = objectives.length;

          return (
            <div
              key={q.id}
              className={cn(
                "p-2.5 rounded-lg border space-y-1.5 transition-colors",
                q.status === "complete" ? "bg-primary/5 border-primary/20" :
                q.status === "failed" ? "bg-destructive/5 border-destructive/20" :
                q.status === "available" && !q.player_accepted ? "bg-accent/10 border-accent/30" :
                "bg-muted/20 border-border/40 hover:border-primary/30",
              )}
            >
              <div className="flex items-start gap-1.5">
                {q.status === "complete" ? <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /> :
                 q.status === "failed" ? <XCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" /> :
                 <Circle className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-foreground leading-tight">{q.title}</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 capitalize">
                      {q.category === "player_created" ? t.play_personal : q.category}
                    </Badge>
                    {totalCount > 0 && (
                      <span className="text-[9px] text-muted-foreground">{doneCount}/{totalCount}</span>
                    )}
                    {isMultiplayer && q.accepted_player_count && q.accepted_player_count > 0 && (
                      <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                        <Users className="w-2.5 h-2.5" /> {q.accepted_player_count}
                      </span>
                    )}
                  </div>
                </div>
                {(q.status === "available" || (q.status === "active" && !q.player_accepted)) && useDbQuests && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[9px] gap-0.5 px-1.5 border-primary/30 text-primary hover:bg-primary/10"
                    onClick={() => handleAcceptQuest(q)}
                    disabled={loading}
                  >
                    <UserCheck className="w-3 h-3" /> {t.play_accept}
                  </Button>
                )}
              </div>
              {q.description && <p className="text-[11px] text-muted-foreground pl-5 leading-relaxed">{q.description}</p>}
              {q.rewards && q.status !== "available" && (
                <p className="text-[10px] text-primary/70 pl-5">{t.play_rewards}: {q.rewards}</p>
              )}
              {objectives.length > 0 && (
                <div className="pl-5 space-y-1 pt-0.5">
                  {objectives.map((obj, j) => (
                    <div key={j} className="flex items-center gap-1.5 text-[10px]">
                      {obj.done
                        ? <CheckCircle2 className="w-2.5 h-2.5 text-primary shrink-0" />
                        : <Circle className="w-2.5 h-2.5 text-muted-foreground/50 shrink-0" />}
                      <span className={cn(obj.done ? "line-through text-muted-foreground" : "text-foreground")}>{obj.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
